import React, { useCallback, useContext, useEffect, useState } from 'react';

// REDUX
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
	protocoloFicRequest,
	protocoloFicClear,
} from 'store/modules/api/iirgd/protocoloFic/actions';
import { baixarArquivosClear } from 'store/modules/api/utils/arquivos/baixarArquivos/actions';

// COMPONENTS
import ButtonImage from 'components/Common/ButtonImage';

// UTILS
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce';
import PDFBlobViewer from 'components/Common/PDFBlobViewer';
import { formatarProtocoloFIC, getSedex, getTipoCili } from './functions';

interface Props {
	clicked?: () => void;
}

export const ImprimirProtocolo: React.FC<Props> = ({ clicked }) => {
	const [clickReimpressao, setClickReimpressao] = useState(false);

	const dispatch = useDispatch();
	const { token } = useContext<IAuthContext>(AuthContext);

	const { login } = useSelector(
		(state: ApplicationState) => state.api.sgu.loginUnico,
	);

	const {
		cadastroRg,
		consultaPesquisaCirg,
		consultaPesquisaCriminal,
		consultaPesquisaCidadao,
		scriptPreAtendimento,
		liberacaoCili,
	} = useSelector((state: ApplicationState) => state.api.iirgd);

	const { disponibilzarDare } = useSelector(
		(state: ApplicationState) => state.api.potencial,
	);

	const { confrontarDigitaisIIRGD, consultarDigitaisIIRGD, baixarArquivos } =
		useSelector((state: ApplicationState) => state.api.utils);

	const { capturarBiometria } = useSelector(
		(state: ApplicationState) => state.api.biometria,
	);

	const { dadosAgendamento } = useSelector(
		(state: ApplicationState) => state.api.sgu.atendimento,
	);

	const getChanges = useCallback(() => {
		let response = '';
		if (scriptPreAtendimento.adocao) {
			response = `AD`;
		}
		if (scriptPreAtendimento.paternidadeReconhecida) {
			response = `${response !== '' ? `${response} | ` : ''}PM`;
		}
		if (scriptPreAtendimento.tranGenero) {
			response = `${response !== '' ? `${response} | ` : ''}TG`;
		}
		if (scriptPreAtendimento.outros) {
			response = `${response !== '' ? `${response} | ` : ''}O`;
		}
		return response;
	}, [
		scriptPreAtendimento.adocao,
		scriptPreAtendimento.paternidadeReconhecida,
		scriptPreAtendimento.tranGenero,
		scriptPreAtendimento.outros,
	]);

	useEffect(() => {
		dispatch(protocoloFicClear());
		dispatch(baixarArquivosClear());
	}, [dispatch]);

	const imprimirProtocolo = useCallback(async () => {
		let payload = formatarProtocoloFIC(
			login,
			cadastroRg,
			getSedex(consultaPesquisaCirg, cadastroRg),
			disponibilzarDare.data?.codigo ?? '',
			consultaPesquisaCriminal.restricaoCriminal,
			getTipoCili(
				consultaPesquisaCidadao,
				cadastroRg,
				confrontarDigitaisIIRGD,
				capturarBiometria,
				consultarDigitaisIIRGD,
				consultaPesquisaCriminal.restricaoCriminal,
			),
			confrontarDigitaisIIRGD.resultado,
			dadosAgendamento,
			liberacaoCili.data ? liberacaoCili.data.codigoRetorno === '00' : false,
		);
		if (
			payload &&
			scriptPreAtendimento.preAtendimentoValido &&
			scriptPreAtendimento.teveAlteracao
		) {
			const alterouNome = getChanges();
			payload = { ...payload, cidadao: { ...payload.cidadao, alterouNome } };
		}
		if (payload) dispatch(protocoloFicRequest(token, payload));
		setClickReimpressao(true);
	}, [
		liberacaoCili,
		cadastroRg,
		capturarBiometria,
		confrontarDigitaisIIRGD,
		consultaPesquisaCidadao,
		consultaPesquisaCirg,
		consultaPesquisaCriminal.restricaoCriminal,
		dispatch,
		disponibilzarDare,
		getChanges,
		login,
		scriptPreAtendimento.preAtendimentoValido,
		scriptPreAtendimento.teveAlteracao,
		dadosAgendamento,
		consultarDigitaisIIRGD,
		token,
	]);

	const handleClick = useCallback(() => {
		imprimirProtocolo();
	}, [imprimirProtocolo]);

	const handleOnClose = useCallback(() => {
		baixarArquivosClear();
		if (clicked) clicked();
	}, [clicked]);

	return (
		<>
			<ButtonImage
				type="button"
				src="imprimir-protocolo"
				onClick={handleClick}
			/>

			{clickReimpressao &&
				baixarArquivos.instanceRequest === 'protocolo_fic' &&
				baixarArquivos.data.arquivo && (
					<PDFBlobViewer
						title="PROTOCOLO PARA SIMPLES CONFERÊNCIA DE DADOS"
						source={baixarArquivos.data.arquivo}
						onClose={handleOnClose}
					/>
				)}
		</>
	);
};
